import { useState, useCallback, useMemo } from 'react';
import {
  Send,
  Loader2,
  ClipboardList,
  CheckCircle2,
  XCircle,
  RotateCcw,
  Lightbulb,
  ListOrdered,
  Trophy,
  ChevronRight,
} from 'lucide-react';
import { submitQuizAttempt } from '../../services/labApi';

// Ported & rewritten from CHEM2131's QuizPanel to match the ACE backend contract,
// then upgraded for college-level assessment: progressive per-question hints,
// step-by-step worked solutions revealed after answering, numeric tolerance with
// explicit unit feedback, immediate per-question explanation, a running score +
// progress bar, retry, and a results summary.
//
// Backward compatibility: QuizChoice / QuizQuestion / QuizConfig keep all their
// original fields; every new field is OPTIONAL and additive. The `quiz` and
// `onComplete` props are unchanged. WidgetRenderer.normalizeQuestions produces
// exactly these shapes — nothing it emits is broken by the additions.

export interface QuizChoice {
  id: string;
  text: string;
}

export interface QuizQuestion {
  id?: string;
  kind: 'mcq' | 'numeric' | 'short';
  prompt: string;
  choices?: QuizChoice[];
  // Local-grading hints (optional; server is authoritative when id is present):
  correct?: unknown; // mcq: choiceId | numeric: number | short: string|string[]
  tolerance?: number; // numeric, absolute
  unit?: string; // numeric display + unit feedback
  explanation?: string;
  points?: number;
  // ── Additive college-depth fields (all optional) ──────────────────────────
  /** Progressive hints, revealed one at a time before answering. */
  hints?: string[];
  /** Ordered worked-solution steps, revealed after the question is graded. */
  solution?: string[];
  /** Difficulty tag, shown as a small chip. */
  difficulty?: 'intro' | 'core' | 'challenge';
}

export interface QuizConfig {
  title?: string;
  questions: QuizQuestion[];
}

interface Props {
  quiz: QuizConfig;
  onClose?: () => void;
  onComplete?: (result: { correct: number; total: number; score: number }) => void;
}

interface GradedResult {
  correct: boolean | null; // null = ungraded (short answer without server)
  score: number;
  explanation?: string | null;
  /** Numeric-only: signed delta from the expected value, for unit feedback. */
  delta?: number | null;
}

const DIFFICULTY_META: Record<NonNullable<QuizQuestion['difficulty']>, { label: string; cls: string }> = {
  intro: { label: 'Intro', cls: 'text-ws-success border-ws-success/40' },
  core: { label: 'Core', cls: 'text-ws-accent border-ws-accent/40' },
  challenge: { label: 'Challenge', cls: 'text-ws-warning border-ws-warning/40' },
};

// Local fallback grading when a question has no server-side id.
function gradeLocally(q: QuizQuestion, answer: string): GradedResult {
  const points = q.points ?? 1;
  if (q.kind === 'mcq') {
    const correct = answer === String(q.correct ?? '');
    return { correct, score: correct ? points : 0, explanation: q.explanation };
  }
  if (q.kind === 'numeric') {
    const num = parseFloat(answer);
    if (Number.isNaN(num)) return { correct: false, score: 0, explanation: 'Enter a number.' };
    const expected = Number(q.correct ?? 0);
    const tol = Math.abs(q.tolerance ?? 0);
    // Absolute tolerance, matching the backend: |answer - expected| <= tolerance.
    const correct = Math.abs(num - expected) <= tol;
    const unit = q.unit ? ' ' + q.unit : '';
    return {
      correct,
      score: correct ? points : 0,
      delta: num - expected,
      explanation:
        q.explanation ??
        (correct
          ? `Correct — within ±${tol}${unit} of ${expected}${unit}.`
          : `Expected ${expected}${unit} (±${tol}${unit}).`),
    };
  }
  // short answer: keyword/substring match if a correct value is given, else ungraded.
  if (q.correct != null) {
    const keys = (Array.isArray(q.correct) ? q.correct : [q.correct]).map((k) =>
      String(k).toLowerCase().trim(),
    );
    const a = answer.toLowerCase().trim();
    const correct = keys.some((k) => a.includes(k));
    return { correct, score: correct ? points : 0, explanation: q.explanation };
  }
  return { correct: null, score: 0, explanation: q.explanation };
}

export default function QuizPanel({ quiz, onClose, onComplete }: Props) {
  const [answers, setAnswers] = useState<Record<number, string>>({});
  const [results, setResults] = useState<Record<number, GradedResult> | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Per-question count of revealed hints, and per-question solution toggle.
  const [hintsShown, setHintsShown] = useState<Record<number, number>>({});
  const [solutionOpen, setSolutionOpen] = useState<Record<number, boolean>>({});

  const setAnswer = useCallback((idx: number, value: string) => {
    setAnswers((prev) => ({ ...prev, [idx]: value }));
  }, []);

  const revealHint = useCallback((idx: number, total: number) => {
    setHintsShown((prev) => ({ ...prev, [idx]: Math.min((prev[idx] ?? 0) + 1, total) }));
  }, []);

  const toggleSolution = useCallback((idx: number) => {
    setSolutionOpen((prev) => ({ ...prev, [idx]: !prev[idx] }));
  }, []);

  const totalPoints = useMemo(
    () => quiz.questions.reduce((sum, q) => sum + (q.points ?? 1), 0),
    [quiz.questions],
  );

  const handleSubmit = async () => {
    const unanswered = quiz.questions.filter((_, i) => !answers[i] || answers[i].trim() === '');
    if (unanswered.length > 0) {
      setError(`Please answer all questions (${unanswered.length} remaining).`);
      return;
    }
    setError(null);
    setSubmitting(true);

    const graded: Record<number, GradedResult> = {};
    await Promise.all(
      quiz.questions.map(async (q, i) => {
        const answer = answers[i];
        if (q.id) {
          try {
            const r = await submitQuizAttempt(q.id, q.kind === 'numeric' ? Number(answer) : answer);
            const local = gradeLocally(q, answer); // for delta / unit feedback only
            graded[i] = {
              correct: r.correct,
              score: r.score,
              explanation: r.explanation ?? q.explanation,
              delta: local.delta,
            };
            return;
          } catch {
            // Server unavailable — fall through to local grading.
          }
        }
        graded[i] = gradeLocally(q, answer);
      }),
    );

    setResults(graded);
    setSubmitting(false);

    const total = quiz.questions.length;
    const correct = Object.values(graded).filter((g) => g.correct === true).length;
    const score = Object.values(graded).reduce((sum, g) => sum + g.score, 0);
    onComplete?.({ correct, total, score });
  };

  const handleRetry = () => {
    setAnswers({});
    setResults(null);
    setError(null);
    setHintsShown({});
    setSolutionOpen({});
  };

  const answeredCount = quiz.questions.filter((_, i) => answers[i]?.trim()).length;
  const showResults = results !== null;

  // Results summary stats.
  const summary = useMemo(() => {
    if (!results) return null;
    const vals = Object.values(results);
    const correct = vals.filter((g) => g.correct === true).length;
    const gradeable = vals.filter((g) => g.correct !== null).length;
    const score = vals.reduce((s, g) => s + g.score, 0);
    const pct = gradeable > 0 ? Math.round((correct / gradeable) * 100) : 0;
    return { correct, gradeable, score, pct, total: quiz.questions.length };
  }, [results, quiz.questions.length]);

  return (
    <div className="flex w-full max-w-2xl flex-col overflow-hidden rounded-lg border border-ws-border bg-ws-surface shadow-lg">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-ws-border bg-ws-surface-2 px-4 py-2">
        <div className="flex items-center gap-2 text-ws-accent">
          <ClipboardList size={16} />
          <h3 className="m-0 text-sm font-semibold text-ws-ink">{quiz.title ?? 'Quiz'}</h3>
        </div>
        {showResults && (
          <button
            type="button"
            onClick={handleRetry}
            className="flex items-center gap-1.5 rounded-sm px-2 py-1 text-xs font-medium text-ws-ink-2 transition-colors hover:bg-ws-surface"
          >
            <RotateCcw size={13} /> Retry
          </button>
        )}
      </div>

      {/* Progress bar (pre-results: answered; post-results: score) */}
      <div className="h-1 w-full bg-ws-surface-2">
        <div
          className={`h-full transition-[width] duration-300 ease-out ${
            showResults ? 'bg-ws-success' : 'bg-ws-accent'
          }`}
          style={{
            width: showResults
              ? `${summary ? (summary.score / Math.max(totalPoints, 1)) * 100 : 0}%`
              : `${(answeredCount / Math.max(quiz.questions.length, 1)) * 100}%`,
          }}
        />
      </div>

      {/* Results summary banner */}
      {showResults && summary && (
        <div
          role="status"
          aria-live="polite"
          className="flex items-center gap-4 border-b border-ws-border bg-ws-accent-quiet px-4 py-3"
        >
          <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-ws-surface text-ws-accent">
            <Trophy size={22} className={summary.pct >= 80 ? 'text-ws-warning' : 'text-ws-accent'} />
          </div>
          <div className="min-w-0 flex-1">
            <p className="m-0 text-sm font-semibold text-ws-ink">
              {summary.pct >= 80
                ? 'Excellent work'
                : summary.pct >= 50
                  ? 'Good effort — review the gaps below'
                  : 'Keep at it — work the solutions below'}
            </p>
            <p className="m-0 mt-0.5 text-xs text-ws-ink-2">
              {summary.correct}/{summary.gradeable} correct · {summary.score}/{totalPoints} points
              {summary.gradeable < summary.total && (
                <span className="text-ws-ink-3">
                  {' '}
                  · {summary.total - summary.gradeable} for review
                </span>
              )}
            </p>
          </div>
          <div className="flex-shrink-0 text-right">
            <div className="text-2xl font-bold leading-none text-ws-accent-ink">{summary.pct}%</div>
          </div>
        </div>
      )}

      {/* Questions */}
      <div className="flex max-h-[70vh] flex-col gap-3 overflow-y-auto p-3">
        {quiz.questions.map((q, i) => {
          const result = results?.[i];
          const hintCount = q.hints?.length ?? 0;
          const shownHints = hintsShown[i] ?? 0;
          const diff = q.difficulty ? DIFFICULTY_META[q.difficulty] : null;
          return (
            <div key={q.id ?? i} className="flex items-start gap-2">
              <div className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-sm bg-ws-accent text-[11px] font-bold text-white">
                Q{i + 1}
              </div>
              <div className="min-w-0 flex-1">
                <div className="mb-2 flex items-start justify-between gap-2">
                  <p className="m-0 text-sm leading-snug text-ws-ink">{q.prompt}</p>
                  <div className="flex flex-shrink-0 items-center gap-1.5">
                    {diff && (
                      <span
                        className={`rounded-md border px-1.5 py-0.5 text-[10px] font-medium ${diff.cls}`}
                      >
                        {diff.label}
                      </span>
                    )}
                    {(q.points ?? 1) > 1 && (
                      <span className="rounded-md bg-ws-surface-2 px-1.5 py-0.5 text-[10px] font-medium text-ws-ink-3">
                        {q.points} pts
                      </span>
                    )}
                  </div>
                </div>

                {q.kind === 'mcq' && (
                  <div className="flex flex-col gap-1.5">
                    {(q.choices ?? []).map((choice) => {
                      const selected = answers[i] === choice.id;
                      const isCorrectChoice = showResults && String(q.correct ?? '') === choice.id;
                      const isWrongPick = showResults && selected && result?.correct === false;
                      let cls = 'border-ws-border bg-ws-tile hover:border-ws-accent';
                      if (showResults) {
                        if (isCorrectChoice) cls = 'border-ws-success bg-ws-success/10';
                        else if (isWrongPick) cls = 'border-ws-danger bg-ws-danger/10';
                        else cls = 'border-ws-border bg-ws-tile';
                      } else if (selected) {
                        cls = 'border-ws-accent bg-ws-accent-quiet';
                      }
                      return (
                        <button
                          key={choice.id}
                          type="button"
                          disabled={showResults}
                          aria-pressed={selected}
                          onClick={() => setAnswer(i, choice.id)}
                          className={`flex items-center gap-2.5 rounded-sm border px-3 py-1.5 text-left text-sm text-ws-ink outline-none transition-all focus-visible:ring-2 focus-visible:ring-ws-accent ${cls}`}
                        >
                          <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-ws-surface-2 text-[11px] font-semibold">
                            {choice.id.toUpperCase()}
                          </span>
                          {choice.text}
                          {showResults && isCorrectChoice && (
                            <CheckCircle2 size={14} className="ml-auto text-ws-success" />
                          )}
                          {showResults && isWrongPick && (
                            <XCircle size={14} className="ml-auto text-ws-danger" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}

                {q.kind === 'numeric' && (
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      step="any"
                      inputMode="decimal"
                      disabled={showResults}
                      aria-label={`Answer for question ${i + 1}`}
                      value={answers[i] ?? ''}
                      onChange={(e) => setAnswer(i, e.target.value)}
                      className={`w-40 rounded-sm border bg-ws-tile px-3 py-1.5 text-sm text-ws-ink outline-none transition-colors focus:border-ws-accent ${
                        showResults
                          ? result?.correct
                            ? 'border-ws-success'
                            : 'border-ws-danger'
                          : 'border-ws-border'
                      }`}
                    />
                    {q.unit && <span className="text-sm text-ws-ink-2">{q.unit}</span>}
                    {!showResults && q.tolerance != null && q.tolerance > 0 && (
                      <span className="text-xs text-ws-ink-4">
                        tolerance ±{q.tolerance}
                        {q.unit ? ` ${q.unit}` : ''}
                      </span>
                    )}
                  </div>
                )}

                {q.kind === 'short' && (
                  <textarea
                    rows={2}
                    disabled={showResults}
                    aria-label={`Answer for question ${i + 1}`}
                    value={answers[i] ?? ''}
                    onChange={(e) => setAnswer(i, e.target.value)}
                    placeholder="Type your answer…"
                    className="w-full resize-y rounded-sm border border-ws-border bg-ws-tile px-3 py-1.5 text-sm text-ws-ink outline-none transition-colors focus:border-ws-accent"
                  />
                )}

                {/* Progressive hints — only before grading. */}
                {!showResults && hintCount > 0 && (
                  <div className="mt-2 space-y-1.5">
                    {(q.hints ?? []).slice(0, shownHints).map((h, hi) => (
                      <div
                        key={hi}
                        className="flex items-start gap-1.5 rounded-sm border-l-2 border-ws-warning bg-ws-warning/10 px-2.5 py-1.5 text-xs leading-relaxed text-ws-ink-2"
                      >
                        <Lightbulb size={13} className="mt-0.5 flex-shrink-0 text-ws-warning" />
                        <span>
                          <span className="font-medium text-ws-ink">Hint {hi + 1}:</span> {h}
                        </span>
                      </div>
                    ))}
                    {shownHints < hintCount && (
                      <button
                        type="button"
                        onClick={() => revealHint(i, hintCount)}
                        className="flex items-center gap-1 rounded-sm px-2 py-1 text-xs font-medium text-ws-accent transition-colors hover:bg-ws-accent-quiet"
                      >
                        <Lightbulb size={12} />
                        {shownHints === 0 ? 'Need a hint?' : `Another hint (${shownHints}/${hintCount})`}
                      </button>
                    )}
                  </div>
                )}

                {/* Per-question feedback after grading. */}
                {showResults && result && (
                  <div className="mt-2 space-y-1.5">
                    {result.correct === null ? (
                      <div className="flex items-center gap-1.5 text-xs font-medium text-ws-ink-2">
                        <CheckCircle2 size={13} /> Answer submitted for review
                      </div>
                    ) : (
                      <div
                        className={`flex items-center gap-1.5 text-xs font-medium ${
                          result.correct ? 'text-ws-success' : 'text-ws-danger'
                        }`}
                      >
                        {result.correct ? <CheckCircle2 size={13} /> : <XCircle size={13} />}
                        {result.correct ? 'Correct' : 'Incorrect'}
                        {/* Numeric unit feedback: how far off, with units. */}
                        {q.kind === 'numeric' &&
                          !result.correct &&
                          result.delta != null &&
                          Number.isFinite(result.delta) && (
                            <span className="font-normal text-ws-ink-3">
                              ({result.delta > 0 ? 'over' : 'under'} by{' '}
                              {Math.abs(result.delta).toPrecision(3)}
                              {q.unit ? ` ${q.unit}` : ''})
                            </span>
                          )}
                      </div>
                    )}
                    {result.explanation && (
                      <div className="rounded-sm border-l-2 border-ws-accent bg-ws-accent-quiet px-3 py-2 text-xs leading-relaxed text-ws-ink-2">
                        {result.explanation}
                      </div>
                    )}

                    {/* Step-by-step worked solution. */}
                    {q.solution && q.solution.length > 0 && (
                      <div className="rounded-sm border border-ws-border bg-ws-tile">
                        <button
                          type="button"
                          onClick={() => toggleSolution(i)}
                          aria-expanded={!!solutionOpen[i]}
                          className="flex w-full items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-ws-ink transition-colors hover:bg-ws-surface-2"
                        >
                          <ListOrdered size={13} className="text-ws-accent" />
                          Worked solution
                          <ChevronRight
                            size={13}
                            className={`ml-auto text-ws-ink-3 transition-transform ${
                              solutionOpen[i] ? 'rotate-90' : ''
                            }`}
                          />
                        </button>
                        {solutionOpen[i] && (
                          <ol className="m-0 list-none space-y-1.5 border-t border-ws-border px-3 py-2">
                            {q.solution.map((step, si) => (
                              <li key={si} className="flex items-start gap-2 text-xs leading-relaxed text-ws-ink-2">
                                <span className="mt-0.5 flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full bg-ws-accent text-[9px] font-bold text-white">
                                  {si + 1}
                                </span>
                                <span>{step}</span>
                              </li>
                            ))}
                          </ol>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer */}
      {!showResults ? (
        <div className="border-t border-ws-border bg-ws-surface-2 px-3 py-2.5">
          {error && (
            <div role="alert" className="mb-2 text-xs text-ws-danger">
              {error}
            </div>
          )}
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs text-ws-ink-3">
              {answeredCount}/{quiz.questions.length} answered
            </span>
            <div className="flex items-center gap-2">
              {onClose && (
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-sm px-3 py-1.5 text-sm font-medium text-ws-ink-2 transition-colors hover:bg-ws-surface"
                >
                  Close
                </button>
              )}
              <button
                type="button"
                onClick={handleSubmit}
                disabled={submitting}
                className="flex items-center gap-1.5 rounded-sm bg-ws-accent px-4 py-1.5 text-sm font-semibold text-white transition-all hover:bg-ws-accent-hover disabled:cursor-not-allowed disabled:opacity-60"
              >
                {submitting ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
                {submitting ? 'Submitting…' : 'Submit'}
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-between gap-2 border-t border-ws-border bg-ws-surface-2 px-3 py-2.5">
          <span className="text-xs text-ws-ink-3">Review your answers above</span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleRetry}
              className="flex items-center gap-1.5 rounded-sm bg-ws-accent px-4 py-1.5 text-sm font-semibold text-white transition-all hover:bg-ws-accent-hover"
            >
              <RotateCcw size={15} /> Try again
            </button>
            {onClose && (
              <button
                type="button"
                onClick={onClose}
                className="rounded-sm px-3 py-1.5 text-sm font-medium text-ws-ink-2 transition-colors hover:bg-ws-surface"
              >
                Close
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
